import { useEffect, useMemo, useRef, useState } from 'react'
import { TableState, TableStatePatch } from '../types'
import { createDefaultState } from './defaultState'
import { storageGet, storageSet } from '../utils/storage'

const buildStorageKey = (tableKey: string) => `mac_table:${tableKey}`

const deepMerge = (a: any, b: any) => {
  const out: any = Array.isArray(a) ? [...a] : { ...a }
  Object.keys(b || {}).forEach((k) => {
    const v = (b as any)[k]
    if (v && typeof v === 'object' && !Array.isArray(v)) out[k] = deepMerge(out[k] || {}, v)
    else out[k] = v
  })
  return out
}

export const useTableState = ({
  tableKey,
  initialState,
  state,
  onStateChange,
  persistence
}: {
  tableKey: string
  initialState?: Partial<TableState>
  state?: TableState
  onStateChange?: (s: TableState) => void
  persistence: boolean
}) => {
  const storageKey = useMemo(() => buildStorageKey(tableKey), [tableKey])
  const isControlled = !!state
  const hydrated = useRef(false)

  const [internal, setInternal] = useState<TableState>(() => {
    const base = createDefaultState()
    const persisted = persistence ? storageGet(storageKey) : null
    return deepMerge(base, deepMerge(persisted || {}, initialState || {}))
  })

  const current = (state || internal) as TableState

  const setState = (patch: TableStatePatch | ((s: TableState) => TableStatePatch)) => {
    const nextPatch = typeof patch === 'function' ? patch(current) : patch
    const next = deepMerge(current, nextPatch) as TableState
    if (!isControlled) setInternal(next)
    onStateChange?.(next)
    if (persistence) storageSet(storageKey, next)
  }

  useEffect(() => {
    if (!persistence) return
    if (hydrated.current) return
    hydrated.current = true
    const persisted = storageGet(storageKey)
    if (!persisted) return
    const next = deepMerge(current, persisted) as TableState
    if (!isControlled) setInternal(next)
    onStateChange?.(next)
  }, [storageKey])

  return { state: current, setState }
}
